import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { 
  Box, 
  Title, 
  Paper, 
  Group, 
  Text, 
  Button, 
  Badge,
  Skeleton,
  Alert,
  Tabs,
  LoadingOverlay,
} from '@mantine/core';
import { 
  IconAlertCircle, 
  IconArrowLeft, 
  IconCheck, 
  IconX,
  IconKey,
  IconSettings,
  IconArrowsLeftRight,
  IconBrain,
  IconHistory,
  IconShieldCheck,
  IconShieldX,
  IconShieldOff,
  IconTestPipe
} from '@tabler/icons';
import { useIntegration, useIntegrationConnection, useIntegrationConfig, useIntegrationApiKey } from '../../../../hooks/useIntegrations';
import { generateOAuthUrl, validateSubdomain } from '../utils/oauthUrlGenerator';
import { AuthType } from '../types';
import { SourceIcon } from '../../../../utils/SourceIcon';
import { AuthenticationTab } from '../components/AuthenticationTab';
import { MappingsTab } from '../components/MappingsTab';
import { SettingsTab } from '../components/SettingsTab';
import { MLActionsTab } from '../components/ml-actions';
import { isBagelAdmin } from '../../../../utils';
import { useAuth0 } from '@auth0/auth0-react';
import { IntegrationSyncRunsTimeline } from '../components/IntegrationSyncRuns';

export const IntegrationDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { key } = useParams<{ key: string }>();
  const defaultTab = key === 'api' ? 'mappings' : 'authentication';
  const activeTab = searchParams.get('tab') || defaultTab;
  const auth0 = useAuth0();
  const isBagelUser = isBagelAdmin(auth0?.user);
  const { 
    data: integration,
    isLoading,
    error 
  } = useIntegration(key || '');

  const {
    disconnect,
    isDisconnecting,
  } = useIntegrationConnection();

  const { mutateAsync: updateConfigMutation, isPending: isUpdatingConfig } = useIntegrationConfig();
  const { mutate: updateApiKey, isPending: isUpdatingApiKey } = useIntegrationApiKey();

  if (isLoading) {
    return (
      <Box p="sm">
        <Group my="sm" position="apart">
          <Skeleton height={25} width={130} />
          <Skeleton height={25} width={130} />
        </Group>
        <Skeleton height={40} width={40} circle />
        <Box my="sm">
          <Skeleton height={28} width={200} mb="sm" />
          <Skeleton height={16} width={300} />
        </Box>
        
        <Skeleton height={200} radius="md" />
      </Box>
    );
  }

  if (error || !integration) {
    return (
      <Box p="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load integration details. Please try again later.
        </Alert>
      </Box>
    );
  }

  const { available, connected } = integration;

  const handleConnect = async (formData: any) => {
    if (!integration?.available) return;

    if (formData.authType === AuthType.API_KEY) {
      try {
        // Create token object with all fields
        const tokenObject = {
          type: AuthType.API_KEY,
          ...formData.apiKeyFields,
          ...(formData.subdomain && { subdomain: formData.subdomain })
        };

        // Use the new API key endpoint
        await updateApiKey({
          key: key || '',
          tokenObject
        });
      } catch (error) {
        console.error('Failed to connect with API key:', error);
      }
      return;
    }

    if (formData.authType === AuthType.OAUTH2) {
      const { oauth } = integration.available;
      try {
        if (oauth?.requiresSubdomain) {
          if (!formData.subdomain) {
            return;
          }

          const validation = validateSubdomain(integration.available, formData.subdomain);
          if (!validation.isValid) {
            return;
          }
        }

        const oauthUrl = generateOAuthUrl(
          integration.available,
          {
            subdomain: formData.subdomain || undefined,
            redirectUri: window.__RUNTIME_CONFIG__.REACT_APP_OAUTH_CALLBACK,
            runtimeConfig: window.__RUNTIME_CONFIG__
          }
        );
        console.log('Debug - OAuth URL:', oauthUrl);

        window.location.href = oauthUrl;
      } catch (error) {
        console.error('Failed to generate OAuth URL:', error);
      }
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect(key || '');
    } catch (error) {
      console.error('Failed to disconnect:', error);
    }
  };

  const handleTabChange = (value: string) => {
    // Update URL with new tab value
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', value);
    navigate(`${location.pathname}?${newSearchParams.toString()}`);
  };

  const isConnected = ['active', 'setup'].includes(connected?.status || '');

  return (
    <Box p="md">
      <LoadingOverlay visible={isLoading} />
      <Group mb="xl" position="apart" align="flex-start">
        <Box>
          <Button 
            variant="subtle" 
            leftIcon={<IconArrowLeft size={16} />}
            color='gray'
            sx={{
              color: 'black',
            }}
            pl={0}
            pr={6}
            onClick={() => navigate('/settings/integrations2.0')}
            size='xs'
          >
            Back
          </Button>
          <Box mt="md">
            <SourceIcon sourceName={available.key} width={40} height={40} />
            <Box>
              <Title order={2}>{available.name}</Title>
              <Text color="dimmed" size="sm">
                {available.description}
              </Text>
            </Box>
          </Box>
        </Box>
        
        <Group spacing="xs">
          <Badge 
            size="lg"
            variant="dot"
            color={isConnected ? 'green' : 'gray'}
            styles={{
              inner: {
                color: isConnected ? '#40c057' : 'gray',
              },
              root: {
                borderColor: isConnected ? '#40c057' : 'gray',
              },
            }}
            // leftSection={connected?.status === 'active' ? 
            //   <IconCheck size={12} /> : 
            //   <IconX size={12} />
            // }
          >
            {isConnected ? 'Connected' : 'Not Connected'}
          </Badge>
        </Group>
      </Group>

      {connected?.status === 'errored' && (
        <Alert 
          icon={<IconAlertCircle size={16} />} 
          title="Connection Issues" 
          color="red"
          mb="md"
        >
          Error connecting to integration
        </Alert>
      )}

      <Paper withBorder>
        <Tabs value={activeTab} onTabChange={handleTabChange} styles={{
          tab: {
            padding: "12px 16px"
          }
        }}>
          <Tabs.List>
            {available.key !== 'api' && <Tabs.Tab value="authentication" icon={<IconKey size={16} />}>Authentication</Tabs.Tab>}
            {available.key !== 'api' && isBagelUser && <Tabs.Tab value="settings" disabled={!isConnected} icon={<IconSettings size={16} />}>Settings</Tabs.Tab>}
            {isBagelUser && <Tabs.Tab value="mappings" disabled={!isConnected} icon={<IconArrowsLeftRight size={16} />}>Object Mappings</Tabs.Tab>}
            {isBagelUser && <Tabs.Tab value="ml-actions" disabled={!isConnected} icon={<IconBrain size={16} />}>ML Actions</Tabs.Tab>}
            {isBagelUser && <Tabs.Tab value="sync-runs" disabled={!isConnected} icon={<IconHistory size={16} />}>Task Runs</Tabs.Tab>}
          </Tabs.List>

          <Box p="md">
            {available.key !== 'api' && (
              <Tabs.Panel value="authentication">
                <AuthenticationTab
                  integration={integration}
                  onConnect={handleConnect}
                  onDisconnect={handleDisconnect}
                  isConnecting={false}
                  isDisconnecting={isDisconnecting}
                  isUpdatingConfig={isUpdatingConfig || isUpdatingApiKey}
                />
              </Tabs.Panel>
            )}

            {available.key !== 'api' && (
              <Tabs.Panel value="settings">
                <SettingsTab integration={integration} />
              </Tabs.Panel>
            )}
            <Tabs.Panel value="mappings">
              <MappingsTab
                integration={integration}
                onSaveMapping={async (mappingData) => {
                  try {
                    await updateConfigMutation({
                      key: integration.available.key,
                      config: {
                        externalObjects: mappingData
                      }
                    });
                  } catch (error) {
                    console.error('Failed to save mapping:', error);
                    // You might want to show an error notification here
                  }
                }}
                isUpdatingMapping={isUpdatingConfig}
              />
            </Tabs.Panel>
            <Tabs.Panel value="ml-actions">
              <MLActionsTab
                integration={integration}
                isLoading={isUpdatingConfig}
                updateExternalObject={async (originObject, updates) => {
                  try {
                    const currentExternalObjects = integration.connected?.externalObjects || [];
                    const updatedExternalObjects = currentExternalObjects.map(obj => 
                      obj.originObject === originObject 
                        ? { ...obj, ...updates }
                        : obj
                    );
                    
                    await updateConfigMutation({
                      key: integration.available.key,
                      config: {
                        externalObjects: updatedExternalObjects
                      }
                    });
                  } catch (error) {
                    console.error('Failed to save ML filter:', error);
                    throw error;
                  }
                }}
              />
            </Tabs.Panel>
            <Tabs.Panel value="sync-runs">
              {activeTab === 'sync-runs' && <IntegrationSyncRunsTimeline integrationKey={integration.available.key} />}
            </Tabs.Panel>
          </Box>
        </Tabs>
      </Paper>
    </Box>
  );
};

export default IntegrationDetail; 